import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { FormattedMessage } from '@artemis/integrations/contentful/utils';
import {
  getIsJoinLoading,
  getIsTeamByIdLoading,
  getTeamLocations,
} from '@artemis/store/teams/selectors';
import TeamSelectionBox from '@artemis/components/Teams/TeamSelectionBox';
import {
  ButtonContainer,
  Container,
  Description,
  List,
  PrimaryButton,
  SecondaryButton,
  Title,
} from './shared';
import {
  logSelectTeamFloorCancelClick,
  logSelectTeamFloorImpression,
  logSelectTeamFloorNextClick,
  logSelectTeamFloorSelectFloorClick,
} from './analytics';

import 'react-loading-skeleton/dist/skeleton.css';

const SelectFloor = ({ teamId, locationId, onNext, onSkip }) => {
  const isJoinLoading = useSelector(getIsJoinLoading);
  const isTeamLoading = useSelector(getIsTeamByIdLoading);
  const locations = useSelector(getTeamLocations);

  const [floors, setFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState();

  const selectedFloor = floors.find(flr => flr.floorId === selectedFloorId);
  const disabled = !selectedFloorId;

  const onSelectFloor = (floorId, rank) => {
    setSelectedFloorId(floorId);
    logSelectTeamFloorSelectFloorClick({
      teamId,
      locationId,
      floorId,
      rank,
    });
  };

  const onNextClick = () => {
    logSelectTeamFloorNextClick({
      teamId,
      locationId,
      floorId: selectedFloorId,
    });
    onNext(selectedFloor);
  };

  const onCancelClick = () => {
    logSelectTeamFloorCancelClick({ teamId, locationId });
    onSkip();
  };

  useEffect(() => {
    const location = locations.find(loc => loc.locationId === locationId);
    setFloors(location?.floors || []);
  }, [locations, locationId]);

  useEffectOnce(() => {
    logSelectTeamFloorImpression({ teamId, locationId });
  });

  return (
    <Container>
      <Title>
        <FormattedMessage entry="teams.selectFloor.title" />
      </Title>

      <Description $medium>
        <FormattedMessage entry="teams.selectFloor.details" />
      </Description>

      {isTeamLoading ? (
        <Skeleton height={80} borderRadius="6px" />
      ) : (
        <List>
          {floors.map(({ floorId, name, secured, memberCount }, index) => (
            <TeamSelectionBox
              key={floorId}
              id={floorId}
              name={name}
              secured={secured}
              caption={
                <FormattedMessage
                  entry="teams.memberCount"
                  values={{ count: memberCount }}
                />
              }
              onSelect={id => onSelectFloor(id, index)}
              selected={selectedFloorId === floorId}
            />
          ))}
        </List>
      )}

      <ButtonContainer>
        <PrimaryButton
          isDisabled={disabled}
          isLoading={isJoinLoading}
          onClick={onNextClick}
        >
          <FormattedMessage entry="teams.nextButton" />
        </PrimaryButton>

        {onSkip && (
          <SecondaryButton isDisabled={isJoinLoading} onClick={onCancelClick}>
            <FormattedMessage entry="teams.skipButton" />
          </SecondaryButton>
        )}
      </ButtonContainer>
    </Container>
  );
};

SelectFloor.propTypes = {
  teamId: PropTypes.string.isRequired,
  locationId: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
};

export default SelectFloor;
